import { useEffect, useState } from "react";
import {
  Drawer,
  Button,
  Input,
  Form,
  message,
  Typography,
  Image,
  Spin,
  Upload,
  Select,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@apollo/client";
import { UPDATE_USER, GET_LOCATIONS } from "./requests";
import { userTypes } from "../../../../types";
import useUpload from "../../../../hooks/use-upload";
import { staffUserTypes } from "../../../../utils/constants";

const { Text } = Typography;
const { Option } = Select;

interface UserDrawerProps {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  refetch: () => void;
  user: typeof userTypes.user | undefined;
}

const EditUser = ({ showModal, setShowModal, refetch, user }: UserDrawerProps) => {
  const [form] = Form.useForm<typeof userTypes.user>();
  const { upload } = useUpload();
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [pageConfig, setPageConfig] = useState({ page: 1,
    pageSize: 15 });
  const [searchName, setSearchName] = useState("");

  const { data, loading: loadingLocations } = useQuery(GET_LOCATIONS, {
    variables: {
      params: pageConfig,
      name: searchName,
    },
  });

  const locations = data?.locations.results ?? [];

  const [updateUser] = useMutation(UPDATE_USER);

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        ...user,
        location: user.location?.id || null,
      });
      setImageUrl(user.img || null);
    }
  }, [user, form]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();

      const payload = {
        variables: {
          id: user?.id,
          user: {
            ...values,
            img: imageUrl ?? "",
          },
        },
      };

      await updateUser(payload);
      await refetch();

      message.success("Usuario actualizado exitosamente");
      onClose();
    } catch (error) {
      console.log(error);
      message.error("Error al actualizar usuario");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async ({ fileList }: any) => {
    if (fileList[0]) {
      const fileName = `users/${new Date().toISOString()}/${fileList[0]?.name}`;
      setUploadLoading(true);
      const url = await upload(fileList[0].originFileObj, fileName);
      setImageUrl(url);
      setUploadLoading(false);
      message.success("Imagen agregada.");
    }
  };

  const onClose = () => {
    setShowModal(false);
  };

  return (
    <Drawer title="Editar Usuario" open={showModal} onClose={onClose} width={"40%"}>
      <Form form={form} layout="vertical">
        <Form.Item name="firstName" label="Nombre" rules={[{ required: true }]}>
          <Input placeholder="Ingrese el nombre" />
        </Form.Item>
        <Form.Item name="lastName" label="Apellido" rules={[{ required: true }]}>
          <Input placeholder="Ingrese el apellido" />
        </Form.Item>
        <Form.Item name="email" label="Correo" rules={[{ required: true }]}>
          <Input placeholder="Ingrese el correo" />
        </Form.Item>
        <Form.Item name="type" label="Tipo" rules={[{ required: true }]}>
          <Select placeholder="Selecciona el tipo" allowClear>
            {Object.keys(staffUserTypes).map((key) => (
              <Option key={key} value={key}>
                {staffUserTypes[key as keyof typeof staffUserTypes].name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="location"
          label="Franquicia"
          rules={[{ required: true,
            message: "Selecciona una Franquicia" }]}
        >
          <Select
            showSearch
            placeholder="Busca o selecciona una Franquicia"
            filterOption={false}
            onSearch={(value) => {
              setSearchName(value);
              setPageConfig({ ...pageConfig,
                page: 1 });
            }}
            loading={loadingLocations}
            allowClear
          >
            {locations.map((loc) => (
              <Option key={loc.id} value={loc.id}>
                {loc.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
      <div>
        <Text>Subir Imagen</Text>
        <Upload
          beforeUpload={() => false}
          listType="picture"
          maxCount={1}
          accept="image/*"
          onChange={(info) => handleFileUpload(info)}
          showUploadList={false}
        >
          <Button style={{ marginLeft: 10 }} icon={<UploadOutlined />}>Seleccionar Imagen</Button>
          {uploadLoading ? <Spin style={{ margin: 10 }} /> : null}
        </Upload>
        {imageUrl && <Image style={{ marginLeft: 20 }} width={140} src={imageUrl} />}
      </div>
      <Button size="large" loading={loading} type="primary" onClick={handleSubmit} style={{ marginTop: 40 }}>
        Actualizar Usuario
      </Button>
    </Drawer>
  );
};

export default EditUser;
